/**
 * Petro-Pass USA Bulk Margin Manager (Phase 3E-3, Feb 2026).
 *
 * Admin-only screen that lists every active customer's Petro-Pass USA
 * `country_default` margin rule (¢/GAL) alongside their station-override
 * count. Individual edits reuse `PUT /api/margins/{id}` so the audit
 * trail is identical to CustomerDetail edits. Bulk apply hits the
 * dedicated `POST /api/admin/petro-pass-us-margins/bulk-update` endpoint
 * with an explicit confirmation payload.
 *
 * NEVER creates a new margin rule from this screen. Missing default
 * rules for a customer are shown as "no rule" so operators can add
 * them via the existing CustomerDetail flow. Portal visibility
 * (`price_report_networks`) is NOT edited here — that remains a
 * separate operator action.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import BackdatedConfirmModal from "../../components/BackdatedConfirmModal";

// Locked "read-only supplier index for admin preview" — TChek 520712,
// AL (PETRO GADSDEN), 2026-08-25 · $5.4190 USD/GAL. Used purely as a
// live-preview example on the edit modal; never shipped to customers.
const LOCKED_TCHEK = "520712";
const LOCKED_INDEX_USD_GAL = 5.419;

function DollarsPerGal(v) {
  if (typeof v !== "number" || Number.isNaN(v)) return "—";
  return `${v.toFixed(4)} USD/GAL`;
}
function CentsPerGal(v) {
  if (typeof v !== "number" || Number.isNaN(v)) return "—";
  return `${v.toFixed(2)} ¢/GAL`;
}

export default function PetroPassUsMarginManager() {
  const backend = process.env.REACT_APP_BACKEND_URL;
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | different_from_6 | has_overrides | no_overrides | no_default
  const [editing, setEditing] = useState(null); // row being edited
  const [newMargin, setNewMargin] = useState("");
  const [saving, setSaving] = useState(false);

  const [selected, setSelected] = useState(new Set());
  const [bulkMargin, setBulkMargin] = useState("");
  const [bulkConfirm, setBulkConfirm] = useState(false);
  // Phase 3E-9Q.2 — defensive backdated 409 handler (see PP-CA equivalent).
  const [backdated, setBackdated] = useState(null);
  async function _readBackdatedError(resp) {
    if (resp.status !== 409) return null;
    try {
      const body = await resp.clone().json();
      const d = body?.detail;
      if (d?.code === "BACKDATED_MARGIN_CONFIRMATION_REQUIRED") return d;
    } catch { /* not JSON */ }
    return null;
  }

  async function load() {
    setLoading(true);
    try {
      const r = await fetch(`${backend}/api/admin/petro-pass-us-margins`, {
        credentials: "include",
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setRows(data.rows || []);
      setMeta({
        customer_count: data.customer_count,
        default_rule_count: data.default_rule_count,
        missing_default_count: data.missing_default_count,
        distinct_default_values: data.distinct_default_values || [],
      });
    } catch (e) {
      toast.error(`Failed to load margins: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let out = rows;
    if (q) {
      const needle = q.toLowerCase();
      out = out.filter((r) =>
        (r.company_name || "").toLowerCase().includes(needle) ||
        (r.customer_id || "").toLowerCase().includes(needle)
      );
    }
    if (filter === "different_from_6") {
      out = out.filter((r) =>
        r.default_margin_cpl !== null && Math.abs((r.default_margin_cpl || 0) - 6.0) > 1e-9
      );
    } else if (filter === "has_overrides") {
      out = out.filter((r) => (r.override_count || 0) > 0);
    } else if (filter === "no_overrides") {
      out = out.filter((r) => (r.override_count || 0) === 0);
    } else if (filter === "no_default") {
      out = out.filter((r) => r.default_margin_cpl === null);
    }
    return out;
  }, [rows, q, filter]);

  function toggleSelect(cid) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(cid)) next.delete(cid); else next.add(cid);
      return next;
    });
  }
  function selectAllFiltered() {
    const next = new Set(selected);
    filtered.forEach((r) => next.add(r.customer_id));
    setSelected(next);
  }
  function clearSelection() { setSelected(new Set()); }

  async function saveIndividual() {
    if (!editing) return;
    const val = parseFloat(newMargin);
    if (!Number.isFinite(val) || val < 0) {
      toast.error("Enter a valid ¢/GAL value.");
      return;
    }
    setSaving(true);
    try {
      const r = await fetch(
        `${backend}/api/margins/${editing.default_margin_id}`,
        {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ margin_cpl: val }),
        }
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success(`${editing.company_name}: ${editing.default_margin_cpl.toFixed(2)} → ${val.toFixed(2)} ¢/GAL`);
      setEditing(null); setNewMargin("");
      await load();
    } catch (e) {
      toast.error(`Update failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  }

  async function applyBulk() {
    const val = parseFloat(bulkMargin);
    if (!Number.isFinite(val) || val < 0) {
      toast.error("Enter a valid ¢/GAL value.");
      return;
    }
    if (selected.size === 0) {
      toast.error("Select at least one customer.");
      return;
    }
    setSaving(true);
    try {
      const r = await fetch(
        `${backend}/api/admin/petro-pass-us-margins/bulk-update`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            customer_ids: Array.from(selected),
            new_margin_cpl: val,
          }),
        }
      );
      if (!r.ok) {
        const bd = await _readBackdatedError(r);
        if (bd) {
          const val0 = val;
          const cids = Array.from(selected);
          setBackdated({
            open: true, effectiveDate: bd.effective_date,
            contextLabel: `Bulk-set PP-US margin for ${cids.length} customer${cids.length === 1 ? "" : "s"}`,
            customerCount: cids.length, ruleCount: cids.length,
            retry: async () => {
              setBackdated((b) => b && { ...b, busy: true });
              try {
                const r2 = await fetch(
                  `${backend}/api/admin/petro-pass-us-margins/bulk-update?confirm_backdated=true`,
                  { method: "POST", credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ customer_ids: cids, new_margin_cpl: val0 }) },
                );
                if (!r2.ok) throw new Error(`HTTP ${r2.status}`);
                const data2 = await r2.json();
                toast.success(`Bulk: ${data2.updated_count} updated · ${data2.unchanged_count} unchanged · ${data2.skipped_missing_count} missing`);
                setBulkMargin(""); setBulkConfirm(false); clearSelection();
                setBackdated(null);
                await load();
              } catch (e) { toast.error(`Bulk failed: ${e.message}`); setBackdated((b) => b && { ...b, busy: false }); }
            },
          });
          setSaving(false);
          return;
        }
        throw new Error(`HTTP ${r.status}`);
      }
      const data = await r.json();
      toast.success(
        `Bulk: ${data.updated_count} updated · ${data.unchanged_count} unchanged · ${data.skipped_missing_count} missing`
      );
      setBulkMargin(""); setBulkConfirm(false); clearSelection();
      await load();
    } catch (e) {
      toast.error(`Bulk update failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  }

  const editIndex = LOCKED_INDEX_USD_GAL;
  const previewOld = editing && typeof editing.default_margin_cpl === "number"
    ? editIndex + editing.default_margin_cpl / 100 : null;
  const previewNew = editing && Number.isFinite(parseFloat(newMargin))
    ? editIndex + parseFloat(newMargin) / 100 : null;

  return (
    <div className="p-6 space-y-6" data-testid="pp-us-margin-manager">
      <div>
        <h1 className="text-3xl font-semibold">Petro-Pass USA Margins</h1>
        <p className="text-sm text-zinc-500 mt-1">
          Country <b>USA</b> · Network <b>Petro-Pass</b> · Unit <b>¢/GAL</b>.
          Each row is an editable customer-specific margin rule. Portal
          visibility is <b>separate</b> — it is not toggled from this screen.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="gf-card p-4"><div className="overline text-[10px]">Active customers</div><div className="text-2xl font-semibold" data-testid="pp-summary-customer-count">{meta.customer_count ?? "—"}</div></div>
        <div className="gf-card p-4"><div className="overline text-[10px]">With Petro-Pass USA default</div><div className="text-2xl font-semibold" data-testid="pp-summary-with-default">{meta.default_rule_count ?? "—"}</div></div>
        <div className="gf-card p-4"><div className="overline text-[10px]">Missing default</div><div className="text-2xl font-semibold" data-testid="pp-summary-missing">{meta.missing_default_count ?? "—"}</div></div>
        <div className="gf-card p-4"><div className="overline text-[10px]">Distinct margin values</div><div className="text-sm font-mono">{(meta.distinct_default_values || []).map((v) => `${Number(v).toFixed(2)}`).join(", ") || "—"}</div></div>
      </div>

      <div className="gf-card p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[220px]">
          <label className="overline text-[10px]">Search</label>
          <input className="gf-input w-full" placeholder="Customer name or ID"
                 value={q} onChange={(e) => setQ(e.target.value)}
                 data-testid="pp-search" />
        </div>
        <div>
          <label className="overline text-[10px]">Filter</label>
          <select className="gf-input" value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  data-testid="pp-filter">
            <option value="all">All</option>
            <option value="different_from_6">Different from 6.00¢</option>
            <option value="has_overrides">Has station overrides</option>
            <option value="no_overrides">No overrides</option>
            <option value="no_default">Missing default</option>
          </select>
        </div>
        <div className="flex gap-2">
          <button className="gf-btn gf-btn-outline text-sm" onClick={selectAllFiltered}
                  data-testid="pp-select-all-filtered">Select filtered ({filtered.length})</button>
          <button className="gf-btn gf-btn-outline text-sm" onClick={clearSelection}
                  data-testid="pp-clear-selection">Clear ({selected.size})</button>
        </div>
      </div>

      {/* Bulk edit — always requires explicit confirmation. */}
      <div className="gf-card p-4">
        <div className="overline text-[10px] mb-2">Bulk edit selected ({selected.size})</div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="overline text-[10px]">New margin (¢/GAL)</label>
            <input className="gf-input" type="number" step="0.01" min="0"
                   value={bulkMargin} onChange={(e) => setBulkMargin(e.target.value)}
                   placeholder="e.g. 7.50" data-testid="pp-bulk-margin" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={bulkConfirm}
                   onChange={(e) => setBulkConfirm(e.target.checked)}
                   data-testid="pp-bulk-confirm" />
            I have reviewed the selected customers &amp; the new value.
          </label>
          <button className="gf-btn gf-btn-primary text-sm"
                  disabled={saving || !bulkConfirm || selected.size === 0 || !bulkMargin}
                  onClick={applyBulk} data-testid="pp-bulk-apply">
            Apply to {selected.size} customer{selected.size === 1 ? "" : "s"}
          </button>
        </div>
        <div className="text-xs text-zinc-500 mt-2">
          Only customers who already have a Petro-Pass USA default rule are updated.
          Missing rules are reported back — this screen never CREATES a rule.
        </div>
      </div>

      <div className="gf-card overflow-x-auto">
        <table className="gf-table" data-testid="pp-margin-table">
          <thead>
            <tr>
              <th></th>
              <th>Customer</th>
              <th>Customer ID</th>
              <th>Status</th>
              <th>Country</th>
              <th>Network</th>
              <th className="text-right">Default</th>
              <th>Unit</th>
              <th className="text-right">Overrides</th>
              <th>Portal</th>
              <th>Last updated</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={12} className="text-center text-zinc-400 py-10">Loading…</td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan={12} className="text-center text-zinc-400 py-10">No customers match your filters.</td></tr>}
            {!loading && filtered.map((r) => (
              <tr key={r.customer_id} data-testid={`pp-row-${r.customer_id}`}>
                <td><input type="checkbox" checked={selected.has(r.customer_id)}
                           onChange={() => toggleSelect(r.customer_id)}
                           data-testid={`pp-select-${r.customer_id}`} /></td>
                <td className="text-sm font-medium">{r.company_name}</td>
                <td className="font-mono text-xs">{r.customer_id}</td>
                <td className="text-xs">{r.status}</td>
                <td>USA</td>
                <td>Petro-Pass</td>
                <td className="text-right font-mono">
                  {r.default_margin_cpl == null
                    ? <span className="text-rose-600 text-xs">no rule</span>
                    : `${Number(r.default_margin_cpl).toFixed(2)}`}
                </td>
                <td className="text-xs">¢/GAL</td>
                <td className="text-right">{r.override_count}</td>
                <td className="text-xs">{r.portal_visible ? "visible" : "hidden"}</td>
                <td className="text-xs">{r.default_updated_at || r.default_created_at || "—"}</td>
                <td>
                  {r.default_margin_id ? (
                    <button className="gf-btn gf-btn-outline text-xs"
                            onClick={() => { setEditing(r); setNewMargin(String(r.default_margin_cpl ?? "")); }}
                            data-testid={`pp-edit-${r.customer_id}`}>Edit</button>
                  ) : (
                    <a className="gf-btn gf-btn-outline text-xs"
                       href={`/admin/customers/${r.customer_id}`}
                       data-testid={`pp-add-${r.customer_id}`}>Add rule</a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Individual edit modal (single-customer confirm + live preview). */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
             data-testid="pp-edit-modal">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-lg w-full space-y-4">
            <h2 className="text-lg font-semibold">
              Edit Petro-Pass USA margin — {editing.company_name}
            </h2>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><div className="overline text-[10px]">Current</div><div className="font-mono">{CentsPerGal(editing.default_margin_cpl)}</div></div>
              <div>
                <div className="overline text-[10px]">New</div>
                <input type="number" step="0.01" min="0" className="gf-input w-full"
                       value={newMargin} onChange={(e) => setNewMargin(e.target.value)}
                       autoFocus data-testid="pp-edit-margin-input" />
              </div>
            </div>
            {previewOld != null && previewNew != null && (
              <div className="text-xs bg-zinc-50 border rounded p-3 space-y-1">
                <div className="overline text-[10px]">Live preview — TChek {LOCKED_TCHEK} · Petro-Pass USA</div>
                <div>Supplier Index (admin only): <span className="font-mono">{DollarsPerGal(editIndex)}</span></div>
                <div>Current customer price: <span className="font-mono">{DollarsPerGal(previewOld)}</span></div>
                <div>New customer price: <span className="font-mono font-semibold">{DollarsPerGal(previewNew)}</span></div>
                <div>Change: <span className="font-mono">
                  {`${((parseFloat(newMargin) || 0) - editing.default_margin_cpl).toFixed(2)} ¢/GAL`}
                </span></div>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button className="gf-btn gf-btn-outline text-sm"
                      onClick={() => { setEditing(null); setNewMargin(""); }}
                      data-testid="pp-edit-cancel">Cancel</button>
              <button className="gf-btn gf-btn-primary text-sm"
                      disabled={saving} onClick={saveIndividual}
                      data-testid="pp-edit-save">
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Phase 3E-9Q.2 — shared backdated pricing-change guard. */}
      <BackdatedConfirmModal
        open={!!backdated?.open}
        onCancel={() => setBackdated(null)}
        onConfirm={() => backdated?.retry && backdated.retry()}
        effectiveDate={backdated?.effectiveDate}
        contextLabel={backdated?.contextLabel}
        customerCount={backdated?.customerCount}
        ruleCount={backdated?.ruleCount}
        busy={!!backdated?.busy}
      />
    </div>
  );
}
